/* Borderline winter 26/27 · the category shelf (P3 / H3: sections/bl-shelf.liquid, assets/bl-shelf.css).
   Prototype v83 winter-proto/assets/js/shelf.js, on pre-rendered markup, plus the home v2 brand row (spec 2026-10-20 §4 v3):
   - a tab shows its panel, meta row, door and pager (nothing is re-rendered); the rail comes in from the right
     (300ms; none on phones or under reduced motion);
   - on demand (owner 10/21, sections/bl-shelf.liquid lazy_tabs): only the open tab's cards come with the page; a tab
     after it comes with its tile, banner and head and four placeholder cards of the card's geometry
     (snippets/bl-shelf-ghost, data-sort on its panel). Its cards are fetched once — on its first activation, or
     before: a hover / focus / touch of its tab, and one by one in idle time once the shelf comes near (not on a data
     saver) — through the section's tab mode (…/collections/<c1>?sort_by=<data-sort>&section_id=…), and take the
     placeholders' place. While they are on their way and the tab is open its rail waits dimmed (aria-busy); a
     request that fails or has not answered in 10 s leaves the tab failed: a retry note with a button on the first
     placeholder, and the tab's next press asks again too. An empty answer takes the tab away. The tallest-tab
     min-height (below) is measured with the placeholders, so it holds before any tab has come; when one comes it is
     taken again and never lowered;
   - a pill filters the open rail by the data tokens on each card wrapper (groups AND, one word per group, the
     second press clears it); filtered-out cards leave the rail and come back in their order, so P0's pager
     (bl-rail.js: it watches the rail's children) re-pages by itself; the door's filter query and the live region
     follow; the rail's first cell, the tab's feature tile, always stays;
   - brands (data-brands): once the shelf comes near the viewport, each tab's collections are asked for their stocked
     brands (the section's counts mode, one request per collection) and the pills appear after "Featured": the brands
     with at least data-brand-min stocked products, by count, data-brand-max at most. A brand pill fetches that tab's
     brand rail once (the section's brand mode: the brand's feature tile, then the brand's stocked products), keeps
     it, and puts it in the rail in place of the featured cells — the tile first, the brand's banner in place of the
     tab's own above the rail on phones; "Featured" (or the same pill again) brings the tab's own cells and banner
     back. Every press supersedes the rail on its way (its request is aborted, the rail is never left dimmed); a
     request that has not answered in 10 s counts as failed. A failed request falls back to the featured cards of
     that vendor (none: Featured again, the pill released; the next press asks again); a brand with nothing stocked
     here any more shows its featured cards, or loses its pill. A failed brands request is asked again once after
     2 s, then on the tab's next press. The door carries the brand (filter.p.vendor). A tab change returns to
     Featured.
   - the tabs row scrolls the open tab into view (a click, the arrow keys, Home / End, the theme editor);
   - the section keeps its tallest tab state as min-height, so the module below never moves on a tab change (a
     shorter tab's slack sits between the rail and the help line, which keeps the foot; each tab is measured with
     its own help-line words); after the first measure it only grows (fonts, the page's load, the brand pills, a tab
     on demand coming), and only a new width measures from scratch;
   - every tab change dispatches `shelf:show` ({ key, label, tool, toolUrl, sport }); the help line's tool link takes
     the words and the page, the kit tiles (bl-kit-tiles.js) take the sport. */
import { Component } from '@theme/component';

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const phone = matchMedia('(max-width: 767px)');
const still = () => reducedMotion.matches || phone.matches;

/** A brand rail that has not answered by then counts as failed (a dead connection must not hold the rail). */
const RAIL_TIMEOUT = 10000;

/** A failed brands request is asked again once after this, then on the tab's next press. */
const BRANDS_RETRY = 2000;

/** A tab's cards (on demand) that have not come by then count as failed. */
const TAB_TIMEOUT = 10000;

/** The idle prefetch starts when the shelf is this near the viewport (as the brand counts do). */
const NEAR = '1200px 0px';

/**
 * @typedef {object} BrandRow
 * @property {string} vendor - The Shopify vendor.
 * @property {string} name - The brand's display name.
 * @property {number} count - Stocked products.
 * @property {string[]} in - The collection handles that hold some.
 */

/**
 * @typedef {object} BrandRail
 * @property {HTMLElement | null} banner - The phone banner (above the rail).
 * @property {HTMLElement[]} cells - The rail cells: the brand's feature tile, then the cards.
 * @property {string} name - The brand's display name.
 */

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} [tabs] - The category tabs, one per rendered block.
 * @property {HTMLElement[]} [panels] - The rail panels (role=tabpanel).
 * @property {HTMLElement[]} [metas] - The pills row (+ pager) of each tab.
 * @property {HTMLAnchorElement[]} [doors] - "All <category>" of each tab.
 * @property {HTMLElement[]} [navs] - The pager host of each tab.
 * @property {HTMLElement[]} [brandRows] - The brand pills row of each tab (brands on).
 * @property {HTMLElement} [h2] - The visually hidden heading that names the open tab.
 * @property {HTMLElement} [status] - The polite live region.
 * @property {HTMLAnchorElement} [helpTool] - The help line's tool link.
 *
 * @extends Component<Refs>
 */
class BlShelfComponent extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  /** The featured cells of each tab (the feature tile first, then the cards), in their Liquid order. @type {Map<string, HTMLElement[]>} */
  #items = new Map();

  /** The tab's own phone banner of each tab. @type {Map<string, HTMLElement>} */
  #banners = new Map();

  /** The open tab's chosen word per facet group. @type {Record<string, string>} */
  #chosen = {};

  #current = '';

  #width = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resizeTimer;

  /** The open tab's chosen vendor ('' = Featured). */
  #brand = '';

  /** The stocked brands of each collection handle (null: the request failed; a failure is not kept). @type {Map<string, Promise<BrandRow[] | null>>} */
  #counts = new Map();

  /** The tabs whose brands request failed: the timed retry or the tab's next press asks again. @type {Set<string>} */
  #retry = new Set();

  /** @type {Set<ReturnType<typeof setTimeout>>} */
  #retryTimers = new Set();

  /** The fetched brand rails, by "tab|vendor". @type {Map<string, BrandRail>} */
  #rails = new Map();

  /** @type {AbortController | null} */
  #railFetch = null;

  #brandsLoaded = false;

  /** @type {IntersectionObserver | null} */
  #near = null;

  /**
   * The tabs on demand whose cards have not come: 'idle' (not asked yet), 'loading', 'failed'. A tab whose cards came
   * (or that came with the page) is not in it.
   * @type {Map<string, 'idle' | 'loading' | 'failed'>}
   */
  #pending = new Map();

  /** The requests for tabs' cards on their way, by tab. @type {Map<string, Promise<boolean>>} */
  #tabLoads = new Map();

  /** @type {Set<AbortController>} */
  #tabAborts = new Set();

  /** @type {IntersectionObserver | null} */
  #nearTabs = null;

  #prefetching = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #idleTimer;

  /** Each tab's state height as last measured (the section's natural height with that tab open). @type {Map<string, number>} */
  #states = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const tabs = this.refs.tabs || [];
    if (!tabs.length) return;

    for (const panel of this.refs.panels || []) {
      const key = panel.dataset.tab || '';
      const track = this.#trackOf(key);
      if (!track) continue;
      const known = this.#items.get(key);
      if (known) this.#place(track, known);   // a reconnect: the tab's own cells back before reading the rail again
      this.#items.set(
        key,
        /** @type {HTMLElement[]} */ ([...track.querySelectorAll(':scope > .bl-shelf__item:not([data-brand-cell="brand"])')])
      );
      // the tab's own phone banner stays; a brand's goes
      const banners = /** @type {HTMLElement[]} */ ([...panel.querySelectorAll(':scope > [data-brand-banner]')]);
      const own = this.#banners.get(key) || banners.find((banner) => banner.dataset.brandBanner === 'featured');
      for (const banner of banners) {
        if (banner !== own) banner.remove();
      }
      if (own) {
        if (!own.isConnected) track.before(own);
        this.#banners.set(key, own);
      }
      // a tab on demand whose cards have not come (its rail holds the placeholders)
      if (panel.dataset.sort && !this.#pending.has(key) && track.querySelector(':scope > .bl-shelf__ghost')) {
        this.#pending.set(key, 'idle');
      }
    }

    const open = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
    this.#show(open.dataset.tab || '', { quiet: true });
    this.#width = this.getBoundingClientRect().width;
    this.#measure();

    // taken again once the fonts and the page are in, never lower (the shelf may be in view by then; only a new width
    // measures from scratch, #onResize)
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#grow();
    });
    window.addEventListener('load', () => this.#grow(), { signal, once: true });
    window.addEventListener('resize', this.#onResize, { signal, passive: true });
    this.addEventListener('shelf:show', this.#onShow, { signal });
    this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    // a tab on demand is asked for as soon as the shopper heads for it (the press follows ~100–300 ms later)
    this.addEventListener('pointerover', this.#onTabIntent, { signal, passive: true });
    this.addEventListener('focusin', this.#onTabIntent, { signal });
    this.addEventListener('touchstart', this.#onTabIntent, { signal, passive: true });
    this.#watchBrands();
    this.#watchTabs();
    this.#watchTabsHint(signal);
  }

  /**
   * The "more tabs" hint (owner 10/21, phones): shown while tabs remain past the row's right edge, gone at the end
   * of the row or when every tab fits; a tap scrolls the row on by most of its width.
   * @param {AbortSignal} signal
   */
  #watchTabsHint(signal) {
    const list = /** @type {HTMLElement | null} */ (this.querySelector('.bl-shelf__tabs'));
    const hint = /** @type {HTMLElement | null} */ (this.querySelector('[data-bl-tabs-more]'));
    if (!list || !hint) return;
    const update = () => {
      const more = list.scrollLeft + list.clientWidth < list.scrollWidth - 4;
      hint.hidden = !more;
      list.classList.toggle('has-more', more);
      if (!more) return;
      // over the list's own box (its offsetParent is the tabs row, position: relative)
      hint.style.top = `${list.offsetTop}px`;
      hint.style.height = `${list.offsetHeight}px`;
      hint.style.left = `${list.offsetLeft + list.offsetWidth - hint.offsetWidth}px`;
    };
    hint.addEventListener('click', () => list.scrollBy({ left: Math.round(list.clientWidth * 0.7), behavior: 'smooth' }), { signal });
    list.addEventListener('scroll', update, { signal, passive: true });
    window.addEventListener('resize', update, { signal, passive: true });
    document.fonts?.ready.then(() => {
      if (this.isConnected) update();
    });
    update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#railFetch?.abort();
    this.#railFetch = null;
    this.#near?.disconnect();
    this.#near = null;
    this.#nearTabs?.disconnect();
    this.#nearTabs = null;
    this.#prefetching = false;
    clearTimeout(this.#idleTimer);
    for (const controller of this.#tabAborts) controller.abort();
    this.#tabAborts.clear();
    clearTimeout(this.#resizeTimer);
    for (const timer of this.#retryTimers) clearTimeout(timer);
    this.#retryTimers.clear();
  }

  /**
   * A tab was pressed (again: a tab whose cards failed asks for them again).
   * @param {MouseEvent} event
   */
  selectTab(event) {
    const tab = /** @type {HTMLElement} */ (event.target).closest('[role="tab"]');
    if (!(tab instanceof HTMLElement)) return;
    const key = tab.dataset.tab || '';
    if (key !== this.#current) this.#show(key);
    else if (this.#pending.get(key) === 'failed') this.#loadTab(key);
  }

  /** The retry note's button (a tab whose cards failed): ask for them again; focus goes to the open tab, as the note
   * (and the button in it) leaves while they are on their way. */
  retryTab() {
    this.#tabOf(this.#current)?.focus({ preventScroll: true });
    this.#loadTab(this.#current);
  }

  /**
   * Arrow keys, Home and End move between the tabs (automatic activation).
   * @param {KeyboardEvent} event
   */
  onTabKeydown(event) {
    const tabs = (this.refs.tabs || []).filter((tab) => !tab.hidden);
    const index = tabs.findIndex((tab) => tab.dataset.tab === this.#current);
    let next = -1;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next < 0 || !tabs[next]) return;
    event.preventDefault();
    this.#show(tabs[next].dataset.tab || '');
    tabs[next].focus({ preventScroll: true });
  }

  /**
   * A filter word was pressed: choose it, or clear it on the second press.
   * @param {MouseEvent} event
   */
  pickWord(event) {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-word]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const group = button.dataset.group || '';
    const word = button.dataset.word || '';
    if (this.#chosen[group] === word) delete this.#chosen[group];
    else this.#chosen[group] = word;
    const shown = this.#filter();
    const track = this.#trackOf(this.#current);
    if (track) {
      this.#home(track);
      this.#animate(track, 'is-fade');
    }
    this.#announce(shown);
  }

  /**
   * A brand pill was pressed: its rail, or the featured rail again (Featured, or the open brand a second time).
   * @param {MouseEvent} event
   */
  pickBrand(event) {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-brand]');
    if (!(button instanceof HTMLButtonElement)) return;
    const vendor = button.dataset.brand || '';
    this.#showBrand(vendor && vendor !== this.#brand ? vendor : '');
  }

  /**
   * Open a tab: its panel, meta row, door and pager show; its filters and brand start clear.
   * @param {string} key - The tab's block id.
   * @param {{ quiet?: boolean }} [options] - quiet: no motion, no announcement, no event (first paint, measuring).
   */
  #show(key, options = {}) {
    const tab = (this.refs.tabs || []).find((item) => item.dataset.tab === key);
    if (!tab) return;
    if (this.#current && this.#current !== key && this.#brand) this.#resetBrand(this.#current);
    this.#current = key;
    this.#chosen = {};
    // the tab's brands request failed earlier: this press asks again
    if (this.#retry.delete(key)) this.#loadTabBrands(key, true);
    for (const item of this.refs.tabs || []) {
      const on = item === tab;
      item.classList.toggle('is-on', on);
      item.setAttribute('aria-selected', String(on));
      item.tabIndex = on ? 0 : -1;
    }
    this.#reveal(key);
    this.#follow(tab);   // after the door swap, which changes the row's width
    const shown = this.#filter();
    const track = this.#trackOf(key);
    if (track) this.#home(track);
    // a tab on demand: its cards are asked for now (or are on their way: the rail waits dimmed)
    if (this.#pending.has(key)) this.#loadTab(key);
    this.#settle(key);
    if (this.refs.h2) this.refs.h2.textContent = tab.dataset.label || '';
    if (options.quiet) return;
    if (track) this.#animate(track, 'is-in');
    this.#announce(Number(this.#byTab(this.refs.metas, key)?.dataset.total) || shown);
    this.#emitShow(tab);
  }

  /**
   * Tell the help line and the kit tiles which tab is open (its sport is blank while a tab on demand has not come;
   * it is told again when the cards bring it).
   * @param {HTMLElement} tab
   */
  #emitShow(tab) {
    this.dispatchEvent(
      new CustomEvent('shelf:show', {
        bubbles: true,
        detail: {
          key: tab.dataset.tab || '',
          label: tab.dataset.label || '',
          tool: tab.dataset.helpTool || '',
          toolUrl: tab.dataset.helpUrl || '',
          sport: tab.dataset.sport || '',
        },
      })
    );
  }

  /**
   * Show only one tab's parts (no other state changes; also used while measuring).
   * @param {string} key
   */
  #reveal(key) {
    for (const list of [this.refs.panels, this.refs.metas, this.refs.doors, this.refs.navs]) {
      for (const element of list || []) element.hidden = element.dataset.tab !== key;
    }
  }

  /**
   * Scroll the tabs row (the row only; the page stays where it is) so the open tab shows whole, the row's padding
   * included: 4px on the left is the focus ring's room, 32px on the right sits under the row's end fade
   * (bl-shelf.css .bl-shelf__tabs).
   * @param {HTMLElement} tab
   */
  #follow(tab) {
    const box = tab.parentElement;
    if (!(box instanceof HTMLElement) || !box.clientWidth || box.scrollWidth <= box.clientWidth) return;
    const style = getComputedStyle(box);
    // the tab's place in the row's scrolled content (rects: no offsetParent assumptions)
    const at = tab.getBoundingClientRect().left - box.getBoundingClientRect().left - box.clientLeft + box.scrollLeft;
    const left = at - (parseFloat(style.paddingLeft) || 0);
    const right = at + tab.getBoundingClientRect().width + (parseFloat(style.paddingRight) || 0);
    if (left < box.scrollLeft) box.scrollLeft = Math.max(0, left);
    else if (right > box.scrollLeft + box.clientWidth) box.scrollLeft = right - box.clientWidth;
  }

  /**
   * Apply the chosen words to the open rail, the pills and the door (the feature tile always stays).
   * @returns {number} How many cards the rail now shows.
   */
  #filter() {
    const key = this.#current;
    const items = this.#items.get(key) || [];
    const chosen = Object.entries(this.#chosen);
    const keep = items.filter(
      (item) =>
        item.hasAttribute('data-brand-cell') ||
        chosen.every(([group, word]) => (item.dataset[group] || '').split(' ').includes(word))
    );
    const track = this.#trackOf(key);
    if (track && !this.#brand) this.#place(track, keep, items);

    const meta = this.#byTab(this.refs.metas, key);
    const words = /** @type {HTMLButtonElement[]} */ ([...(meta?.querySelectorAll('[data-word]') || [])]);
    for (const button of words) {
      const on = this.#chosen[button.dataset.group || ''] === button.dataset.word;
      button.classList.toggle('is-on', on);
      button.setAttribute('aria-pressed', String(on));
    }

    const door = this.#byTab(this.refs.doors, key);
    if (door) {
      let href = door.dataset.base || door.getAttribute('href') || '';
      const query = [];
      for (const button of words) {
        if (this.#chosen[button.dataset.group || ''] !== button.dataset.word) continue;
        if (button.dataset.door) href = button.dataset.door;
        if (button.dataset.param && button.dataset.value) {
          query.push(`${button.dataset.param}=${encodeURIComponent(button.dataset.value)}`);
        }
      }
      if (this.#brand) query.push(`filter.p.vendor=${encodeURIComponent(this.#brand)}`);
      door.href = query.length ? `${href}${href.includes('?') ? '&' : '?'}${query.join('&')}` : href;
    }
    return this.#cards(keep);
  }

  /* ---------- brands ---------- */

  /** Load the brand pills once the shelf comes near the viewport (brands on). */
  #watchBrands() {
    if (this.dataset.brands !== 'true' || this.#brandsLoaded || !this.refs.brandRows?.length) return;
    const start = () => {
      this.#near?.disconnect();
      this.#near = null;
      this.#loadBrands();
    };
    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }
    this.#near = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) start();
      },
      { rootMargin: '1200px 0px' }
    );
    this.#near.observe(this);
  }

  /** Ask every tab's collections for their stocked brands and build the pills, tab by tab as the answers come. */
  #loadBrands() {
    if (this.#brandsLoaded) return;
    this.#brandsLoaded = true;
    for (const panel of this.refs.panels || []) this.#loadTabBrands(panel.dataset.tab || '');
  }

  /**
   * One tab's brand pills from its collections' stocked brands. A failed request (a 5xx, a 429, a dropped
   * connection) is not an answer: it is asked again once after BRANDS_RETRY, then on the tab's next press.
   * @param {string} key
   * @param {boolean} [retried] - A retry: no further timed one.
   */
  #loadTabBrands(key, retried = false) {
    const panel = this.#byTab(this.refs.panels, key);
    if (!panel) return;
    const min = Number(this.dataset.brandMin) || 3;
    const max = Number(this.dataset.brandMax) || 6;
    const signal = this.#controller?.signal;
    const handles = [panel.dataset.c1, panel.dataset.c2].filter((handle) => Boolean(handle));
    Promise.all(handles.map((handle) => this.#countsFor(/** @type {string} */ (handle), signal))).then((lists) => {
      if (!this.isConnected) return;
      if (lists.some((list) => list === null)) {
        this.#retry.add(key);
        if (!retried) {
          const timer = setTimeout(() => {
            this.#retryTimers.delete(timer);
            if (this.isConnected && this.#retry.delete(key)) this.#loadTabBrands(key, true);
          }, BRANDS_RETRY);
          this.#retryTimers.add(timer);
        }
        return;
      }
      /** @type {Map<string, BrandRow>} */
      const sum = new Map();
      lists.forEach((list, index) => {
        for (const row of list || []) {
          if (!row || typeof row.vendor !== 'string') continue;
          const count = Number(row.count) || 0;
          const prev = sum.get(row.vendor) || { vendor: row.vendor, name: row.name || row.vendor, count: 0, in: [] };
          prev.count += count;
          if (count > 0) prev.in.push(/** @type {string} */ (handles[index]));
          sum.set(row.vendor, prev);
        }
      });
      const brands = [...sum.values()]
        .filter((brand) => brand.count >= min)
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
        .slice(0, max);
      this.#renderPills(key, brands);
    });
  }

  /**
   * The stocked brands of one collection (the section's counts mode), fetched once; null when the request failed,
   * which is not kept, so the next ask fetches again.
   * @param {string} handle
   * @param {AbortSignal} [signal]
   * @returns {Promise<BrandRow[] | null>}
   */
  #countsFor(handle, signal) {
    let promise = this.#counts.get(handle);
    if (!promise) {
      const url = `${this.#root()}collections/${handle}?filter.v.availability=1&section_id=${encodeURIComponent(this.dataset.sectionId || '')}`;
      const asked = fetch(url, { credentials: 'same-origin', signal })
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const rows = JSON.parse(doc.querySelector('[data-bl-brands]')?.textContent || '[]');
          return Array.isArray(rows) ? rows : [];
        })
        .catch(() => {
          if (this.#counts.get(handle) === asked) this.#counts.delete(handle);
          return null;
        });
      promise = asked;
      this.#counts.set(handle, promise);
    }
    return promise;
  }

  /**
   * Put a tab's brand pills after "Featured"; the row shows only when there is a brand.
   * @param {string} key
   * @param {BrandRow[]} brands
   */
  #renderPills(key, brands) {
    const row = this.#brandRow(key);
    const chips = row?.querySelector('.bl-shelf__chips');
    if (!row || !chips) return;
    for (const old of chips.querySelectorAll('[data-brand]:not([data-brand=""])')) old.remove();
    for (const brand of brands) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pill bl-shelf__pill';
      button.dataset.brand = brand.vendor;
      button.dataset.name = brand.name;
      button.dataset.in = brand.in.join(' ');
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('on:click', '/pickBrand');
      button.append(brand.name, ' ');
      const small = document.createElement('small');
      small.textContent = String(brand.count);
      button.append(small);
      chips.append(button);
    }
    row.hidden = brands.length === 0;
    // on phones the pills row wraps under the pager row, so the head grew after the first measure: take it again
    // (never lower: the shelf may be in view by now)
    this.#grow();
  }

  /**
   * Show a brand's rail in the open tab, or the tab's own ('').
   * @param {string} vendor
   */
  async #showBrand(vendor) {
    const key = this.#current;
    const track = this.#trackOf(key);
    const panel = this.#byTab(this.refs.panels, key);
    if (!track || !panel) return;
    this.#brand = vendor;
    // this press supersedes the rail on its way: its request goes and so does its dimmed state (a fetch below dims
    // the rail again); every path ends with the rail usable — or waiting for the tab's own cards (on demand), which
    // "Featured" asks for again when they failed
    this.#railFetch?.abort();
    this.#railFetch = null;
    this.#settle(key);
    this.#pressPills(key, vendor);
    if (!vendor && this.#pending.get(key) === 'failed') this.#loadTab(key);

    /** @type {BrandRail | null | undefined} */
    let rail = null;
    if (vendor) {
      const id = `${key}|${vendor}`;
      rail = this.#rails.get(id);
      if (!rail) {
        const controller = new AbortController();
        this.#railFetch = controller;
        this.#busy(track, true);
        // no answer by RAIL_TIMEOUT (a dead connection): abort, and the rail below takes it as a failed request
        const timer = setTimeout(() => controller.abort(), RAIL_TIMEOUT);
        rail = await this.#fetchRail(key, vendor, controller.signal);
        clearTimeout(timer);
        // superseded (another press, a tab change, a disconnect): the newer intent owns the rail and its state
        if (this.#railFetch !== controller || !this.isConnected) return;
        this.#railFetch = null;
        this.#settle(key);
        if (rail) this.#rails.set(id, rail);
        if (this.#brand !== vendor || this.#current !== key) return;
      }
    }

    let shown = 0;
    let name = '';
    const items = this.#items.get(key) || [];
    if (!vendor) {
      this.#place(track, items);
      this.#banner(key, this.#banners.get(key) || null);
      // a tab on demand whose cards are still coming says its category's size, as a tab press does
      shown = this.#pending.has(key) ? Number(this.#byTab(this.refs.metas, key)?.dataset.total) || 0 : this.#cards(items);
    } else if (rail && rail.cells.length > 1) {
      this.#place(track, rail.cells);
      this.#banner(key, rail.banner);
      shown = rail.cells.length - 1;
      name = rail.name;
    } else {
      // no brand rail: the request failed (null: not kept, the next press asks again), or the brand has nothing
      // stocked and pictured in this tab any more (an empty rail). The tab's tile and its featured cards of that
      // vendor stand in; with none of those the tab's own rail comes back with the pill released — never every
      // brand's cards under this brand's name — and a brand with nothing here loses its pill.
      const own = items.filter((item) => item.hasAttribute('data-brand-cell') || (item.dataset.vendor || '') === vendor);
      if (this.#cards(own)) {
        this.#place(track, own);
        shown = this.#cards(own);
        name = this.#brandRow(key)?.querySelector(`[data-brand="${CSS.escape(vendor)}"]`)?.dataset.name || vendor;
      } else {
        this.#brand = '';
        this.#pressPills(key, '');
        if (rail) this.#dropPill(key, vendor);
        this.#place(track, items);
        shown = this.#cards(items);
      }
      this.#banner(key, this.#banners.get(key) || null);
    }
    this.#filter();   // the door takes the brand (none once the pill is released)
    this.#home(track);
    this.#settle(key);
    this.#animate(track, 'is-fade');
    this.#announce(shown, name);
  }

  /**
   * The rail on its way: dimmed and inert (bl-shelf.css .is-busy), or usable again.
   * @param {HTMLElement} track
   * @param {boolean} on
   */
  #busy(track, on) {
    track.classList.toggle('is-busy', on);
    if (on) track.setAttribute('aria-busy', 'true');
    else track.removeAttribute('aria-busy');
  }

  /**
   * A brand with nothing stocked in this tab any more: its pill goes (focus moves to "Featured"), the row with it
   * when it was the last brand.
   * @param {string} key
   * @param {string} vendor
   */
  #dropPill(key, vendor) {
    const row = this.#brandRow(key);
    const pill = row?.querySelector(`[data-brand="${CSS.escape(vendor)}"]`);
    if (!row || !(pill instanceof HTMLElement)) return;
    if (pill.contains(document.activeElement)) {
      /** @type {HTMLElement | null} */ (row.querySelector('[data-brand=""]'))?.focus({ preventScroll: true });
    }
    pill.remove();
    if (!row.querySelector('[data-brand]:not([data-brand=""])')) row.hidden = true;
  }

  /**
   * Back to the tab's own rail without motion (a tab change).
   * @param {string} key
   */
  #resetBrand(key) {
    this.#brand = '';
    this.#railFetch?.abort();
    this.#railFetch = null;
    const track = this.#trackOf(key);
    if (track) this.#place(track, this.#items.get(key) || []);
    this.#settle(key);
    this.#banner(key, this.#banners.get(key) || null);
    this.#pressPills(key, '');
  }

  /**
   * Put one phone banner above a tab's rail (the tab's own or a brand's), in place of whatever is there.
   * @param {string} key
   * @param {HTMLElement | null} banner
   */
  #banner(key, banner) {
    const panel = this.#byTab(this.refs.panels, key);
    const track = this.#trackOf(key);
    if (!panel || !track) return;
    for (const old of panel.querySelectorAll(':scope > [data-brand-banner]')) {
      if (old !== banner) old.remove();
    }
    if (banner && banner.parentElement !== panel) track.before(banner);
  }

  /**
   * @param {string} key
   * @param {string} vendor
   */
  #pressPills(key, vendor) {
    const row = this.#brandRow(key);
    for (const button of row?.querySelectorAll('[data-brand]') || []) {
      const on = (button.getAttribute('data-brand') || '') === vendor;
      button.classList.toggle('is-on', on);
      button.setAttribute('aria-pressed', String(on));
    }
  }

  /**
   * Fetch a tab's brand rail (the section's brand mode) from the tab's collections that hold the brand; the tile
   * comes from the first answer, the cards follow in collection order.
   * @param {string} key
   * @param {string} vendor
   * @param {AbortSignal} signal
   * @returns {Promise<BrandRail | null>}
   */
  async #fetchRail(key, vendor, signal) {
    const panel = this.#byTab(this.refs.panels, key);
    if (!panel) return null;
    const pill = this.#brandRow(key)?.querySelector(`[data-brand="${CSS.escape(vendor)}"]`);
    const stocked = (pill instanceof HTMLElement ? pill.dataset.in || '' : '').split(' ').filter(Boolean);
    const handles = [panel.dataset.c1, panel.dataset.c2].filter(
      (handle) => handle && (!stocked.length || stocked.includes(handle))
    );
    const section = encodeURIComponent(this.dataset.sectionId || '');
    try {
      const docs = await Promise.all(
        handles.map(async (handle) => {
          const url = `${this.#root()}collections/${handle}?filter.p.vendor=${encodeURIComponent(vendor)}&filter.v.availability=1&section_id=${section}`;
          const response = await fetch(url, { credentials: 'same-origin', signal });
          if (!response.ok) throw new Error(String(response.status));
          return new DOMParser().parseFromString(await response.text(), 'text/html');
        })
      );
      /** @type {HTMLElement | null} */
      let banner = null;
      /** @type {HTMLElement | null} */
      let tile = null;
      let name = '';
      /** @type {HTMLElement[]} */
      const cards = [];
      for (const doc of docs) {
        const box = doc.querySelector('.bl-shelf__fetch');
        if (!(box instanceof HTMLElement)) continue;
        name = name || box.dataset.name || '';
        for (const cell of box.querySelectorAll(':scope > [data-brand-rail] > .bl-shelf__item')) {
          if (!(cell instanceof HTMLElement)) continue;
          if (cell.hasAttribute('data-brand-cell')) {
            if (!tile) tile = cell;
          } else {
            cards.push(cell);
          }
        }
        if (!banner) {
          const found = box.querySelector(':scope > [data-brand-banner]');
          if (found instanceof HTMLElement) banner = found;
        }
      }
      if (!tile) return { banner: null, cells: [], name };
      const cells = [tile, ...cards].map((cell) => /** @type {HTMLElement} */ (document.adoptNode(cell)));
      return { banner: banner ? /** @type {HTMLElement} */ (document.adoptNode(banner)) : null, cells, name };
    } catch {
      return null;
    }
  }

  /** The storefront's root path (a locale prefix included). */
  #root() {
    const root = /** @type {{ routes?: { root?: string } } | undefined} */ (window.Shopify)?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  /**
   * A tab's brand pills row (inside its meta row; the row itself carries no tab).
   * @param {string} key
   */
  #brandRow(key) {
    const row = this.#byTab(this.refs.metas, key)?.querySelector('[data-brands-row]');
    return row instanceof HTMLElement ? row : undefined;
  }

  /**
   * How many of these cells are cards (the feature tile and the placeholders left out).
   * @param {HTMLElement[]} cells
   */
  #cards(cells) {
    return cells.filter((cell) => !cell.hasAttribute('data-brand-cell') && !cell.classList.contains('bl-shelf__ghost')).length;
  }

  /* ---------- tabs on demand ---------- */

  /**
   * The shopper heads for a tab (a mouse over it, focus, a touch): a tab on demand is asked for now.
   * @param {Event} event
   */
  #onTabIntent = (event) => {
    if (event.type === 'pointerover' && /** @type {PointerEvent} */ (event).pointerType === 'touch') return;
    const target = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    const key = target instanceof HTMLElement ? target.dataset.tab || '' : '';
    if (key && this.#pending.get(key) === 'idle') this.#loadTab(key);
  };

  /** Once the shelf comes near the viewport, the tabs on demand are fetched one by one in idle time. */
  #watchTabs() {
    if (![...this.#pending.values()].includes('idle')) return;
    const start = () => {
      this.#nearTabs?.disconnect();
      this.#nearTabs = null;
      this.#prefetch();
    };
    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }
    this.#nearTabs = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) start();
      },
      { rootMargin: NEAR }
    );
    this.#nearTabs.observe(this);
  }

  /**
   * The idle prefetch: the tabs after the open one first, one request at a time, each in an idle moment; none on a
   * data saver or a 2G connection (a hover, a focus or a press still asks). A failed tab is left for its press.
   */
  #prefetch() {
    if (this.#prefetching) return;
    const connection = /** @type {{ saveData?: boolean, effectiveType?: string } | undefined} */ (
      /** @type {any} */ (navigator).connection
    );
    if (connection?.saveData || /2g$/.test(connection?.effectiveType || '')) return;
    this.#prefetching = true;
    const next = () => {
      if (!this.isConnected || !this.#prefetching) return;
      const tabs = (this.refs.tabs || []).filter((tab) => !tab.hidden);
      const at = Math.max(0, tabs.findIndex((tab) => tab.dataset.tab === this.#current));
      const tab = [...tabs.slice(at + 1), ...tabs.slice(0, at)].find((item) => this.#pending.get(item.dataset.tab || '') === 'idle');
      if (!tab) {
        this.#prefetching = false;
        return;
      }
      this.#loadTab(tab.dataset.tab || '').then(() => this.#whenIdle(next));
    };
    this.#whenIdle(next);
  }

  /** @param {() => void} callback */
  #whenIdle(callback) {
    clearTimeout(this.#idleTimer);
    this.#idleTimer = setTimeout(() => {
      if ('requestIdleCallback' in window) requestIdleCallback(callback, { timeout: 2000 });
      else callback();
    }, 120);
  }

  /**
   * Fetch a tab's cards on demand, once (a second ask while they are on their way gets the same request): the
   * section's tab mode on the tab's first collection in that collection's default order. A request that fails, has
   * not answered in TAB_TIMEOUT or answers without the tab leaves it failed (asked again on its next press).
   * @param {string} key
   * @returns {Promise<boolean>} Whether the cards came.
   */
  #loadTab(key) {
    if (!this.#pending.has(key)) return Promise.resolve(true);
    const known = this.#tabLoads.get(key);
    if (known) return known;
    const panel = this.#byTab(this.refs.panels, key);
    const handle = panel?.dataset.c1 || '';
    if (!panel || !handle) return Promise.resolve(false);
    const controller = new AbortController();
    this.#tabAborts.add(controller);
    const timer = setTimeout(() => controller.abort(), TAB_TIMEOUT);
    this.#pending.set(key, 'loading');
    this.#settle(key);
    const url = `${this.#root()}collections/${encodeURIComponent(handle)}?sort_by=${encodeURIComponent(panel.dataset.sort || 'manual')}&section_id=${encodeURIComponent(this.dataset.sectionId || '')}`;
    /** @type {Promise<boolean>} */
    const load = fetch(url, { credentials: 'same-origin', signal: controller.signal })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const box = [...doc.querySelectorAll('[data-tab-rail]')].find((element) => element.getAttribute('data-tab-rail') === key);
        if (!(box instanceof HTMLElement)) throw new Error('no tab in the answer');
        if (!this.isConnected) throw new Error('gone');
        this.#fill(key, box);
        return true;
      })
      .catch(() => {
        // a disconnect (aborted) leaves the tab as it was before it was asked for
        if (this.#pending.has(key)) this.#pending.set(key, this.isConnected ? 'failed' : 'idle');
        return false;
      })
      .finally(() => {
        clearTimeout(timer);
        this.#tabAborts.delete(controller);
        if (this.#tabLoads.get(key) === load) this.#tabLoads.delete(key);
        if (!this.isConnected) return;
        this.#settle(key);   // the rail is never left dimmed
        if (this.#pending.get(key) === 'failed' && key === this.#current) {
          const tab = this.#tabOf(key);
          if (this.refs.status && tab) this.refs.status.textContent = `${tab.dataset.label || ''}: ${this.dataset.retryWords || ''}`;
        }
      });
    this.#tabLoads.set(key, load);
    return load;
  }

  /**
   * A tab's cards came: they take the placeholders' place among its own cells (and in its rail, unless a brand's
   * rail is open there); its sport is learnt (told again when it is open); the tallest tab is taken again and the
   * min-height never lowered. An answer without a card takes the tab away.
   * @param {string} key
   * @param {HTMLElement} box - The answer (snippets/bl-shelf-ondemand part 'answer').
   */
  #fill(key, box) {
    const track = this.#trackOf(key);
    const tab = this.#tabOf(key);
    if (!track || !tab) return;
    const cells = /** @type {HTMLElement[]} */ (
      [...box.querySelectorAll(':scope > [data-tab-cells] > .bl-shelf__item')].map((cell) => document.adoptNode(cell))
    );
    this.#pending.delete(key);
    if (!cells.length) {
      this.#dropTab(key);
      return;
    }
    const items = [...(this.#items.get(key) || []).filter((cell) => cell.hasAttribute('data-brand-cell')), ...cells];
    this.#items.set(key, items);
    const sport = box.dataset.sport || '';
    const told = tab.dataset.sport || '';
    tab.dataset.sport = sport;
    const open = key === this.#current;
    const own = !(open && this.#brand);   // the tab's own cells are in its rail (not a brand's rail)
    // after a failure the live region said so: it says the tab again
    const recovered = open && Boolean(this.dataset.retryWords) && (this.refs.status?.textContent || '').endsWith(this.dataset.retryWords || '');
    const before = own ? this.#panelHeight(key) : 0;   // with the placeholders
    for (const ghost of track.querySelectorAll(':scope > .bl-shelf__ghost')) ghost.remove();
    if (own) {
      if (open) {
        const shown = this.#filter();   // the cells and the door
        this.#home(track);
        this.#animate(track, 'is-fade');
        if (recovered) this.#announce(Number(this.#byTab(this.refs.metas, key)?.dataset.total) || shown);
      } else {
        this.#place(track, items);
      }
      // the placeholders have the card's geometry, so the cards are as tall; were they taller (a wrap only a card
      // knows), the min-height takes that tab's state again — measured on its own panel, never by hiding the open
      // one (its rail, its scroll and a swipe on it stay as they are)
      const grew = this.#panelHeight(key) - before;
      if (grew > 0.5) {
        const state = (this.#states.get(key) || 0) + grew;
        this.#states.set(key, state);
        if (state > (parseFloat(this.style.minHeight) || 0)) this.style.minHeight = `${Math.ceil(state)}px`;
      }
    }
    if (open && sport !== told) this.#emitShow(tab);
  }

  /**
   * A tab's panel height. A hidden panel is laid out for a moment out of the flow and unseen (position absolute,
   * visibility hidden, the stage's width), so the open panel is never hidden to measure another.
   * @param {string} key
   */
  #panelHeight(key) {
    const panel = this.#byTab(this.refs.panels, key);
    if (!panel) return 0;
    if (!panel.hidden) return panel.getBoundingClientRect().height;
    const { style } = panel;
    style.position = 'absolute';
    style.insetInline = '0';
    style.top = '0';
    style.visibility = 'hidden';
    panel.hidden = false;
    const height = panel.getBoundingClientRect().height;
    panel.hidden = true;
    style.position = '';
    style.insetInline = '';
    style.top = '';
    style.visibility = '';
    return height;
  }

  /**
   * A tab with nothing to show any more (an empty answer): its tab goes; the first tab opens if it was open.
   * @param {string} key
   */
  #dropTab(key) {
    const tab = this.#tabOf(key);
    if (!tab) return;
    tab.hidden = true;
    if (key === this.#current) {
      const next = (this.refs.tabs || []).find((item) => !item.hidden);
      if (next) {
        this.#show(next.dataset.tab || '');
        next.focus({ preventScroll: true });
      }
    }
    this.#grow();
  }

  /**
   * A tab's rail from its state: dimmed (aria-busy) while a brand's rail is on its way, or while the tab's own cards
   * (on demand) are and its own cells are in the rail; the retry note while they failed; usable otherwise.
   * @param {string} key
   */
  #settle(key) {
    const track = this.#trackOf(key);
    if (!track) return;
    const state = this.#pending.get(key);
    const own = !(key === this.#current && this.#brand);
    this.#busy(track, (key === this.#current && this.#railFetch !== null) || (own && state === 'loading'));
    this.#failed(track, own && state === 'failed');
    // the pager would count the placeholders: it waits, in its place (bl-shelf.css), until the cards come
    this.#byTab(this.refs.navs, key)?.classList.toggle('is-waiting', own && state !== undefined);
  }

  /**
   * The retry note on the first placeholder (words and button from data-retry-words / data-retry-label), or none.
   * @param {HTMLElement} track
   * @param {boolean} on
   */
  #failed(track, on) {
    const ghost = track.querySelector(':scope > .bl-shelf__ghost');
    if (!(ghost instanceof HTMLElement)) return;
    const note = ghost.querySelector(':scope > [data-retry]');
    if (!on) {
      if (note) {
        note.remove();
        ghost.setAttribute('aria-hidden', 'true');
      }
      return;
    }
    if (note) return;
    const box = document.createElement('div');
    box.className = 'bl-shelf__retry';
    box.dataset.retry = '';
    const words = document.createElement('p');
    words.textContent = this.dataset.retryWords || '';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn--sm btn--line bl-shelf__retry-btn';
    button.setAttribute('on:click', '/retryTab');
    button.textContent = this.dataset.retryLabel || 'Try again';
    box.append(words, button);
    // the placeholder's frame stays (aria-hidden on it); the note is what assistive technology meets
    ghost.removeAttribute('aria-hidden');
    for (const part of ghost.children) part.setAttribute('aria-hidden', 'true');
    ghost.append(box);
  }

  /** Take the tallest tab state again, never lower than it was (a tab's cards came: the module below stays). */
  #grow() {
    const was = parseFloat(this.style.minHeight) || 0;
    this.#measure();
    if ((parseFloat(this.style.minHeight) || 0) < was) this.style.minHeight = `${was}px`;
  }

  /** @param {string} key */
  #tabOf(key) {
    return (this.refs.tabs || []).find((item) => item.dataset.tab === key);
  }

  /* ---------- the rail ---------- */

  /**
   * Put exactly these cells in the rail, in this order, ahead of the pager's end pad (.rail__end).
   * Cells already in place are not moved; the rail's other cells (featured or brand) leave.
   * @param {HTMLElement} track
   * @param {HTMLElement[]} keep
   * @param {HTMLElement[]} [all] - Every cell of the rail (defaults to the cells it holds now).
   */
  #place(track, keep, all) {
    const wanted = new Set(keep);
    const every = all || /** @type {HTMLElement[]} */ ([...track.querySelectorAll(':scope > .bl-shelf__item')]);
    for (const item of every) {
      if (!wanted.has(item) && item.parentElement === track) item.remove();
    }
    let cursor = track.firstElementChild;
    for (const item of keep) {
      if (item === cursor) {
        cursor = cursor.nextElementSibling;
        continue;
      }
      track.insertBefore(item, cursor);
    }
  }

  /** @param {HTMLElement} track */
  #home(track) {
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = 0;
    track.style.scrollBehavior = '';
  }

  /**
   * @param {HTMLElement} track
   * @param {'is-in' | 'is-fade'} name
   */
  #animate(track, name) {
    if (still()) return;
    track.classList.add(name);
    void track.offsetWidth;
    track.classList.remove(name);
  }

  /**
   * @param {number} shown
   * @param {string} [brand] - The open brand's name.
   */
  #announce(shown, brand = '') {
    const tab = (this.refs.tabs || []).find((item) => item.dataset.tab === this.#current);
    const meta = this.#byTab(this.refs.metas, this.#current);
    if (!this.refs.status || !tab || !meta) return;
    const noun = shown === 1 ? meta.dataset.nounOne : meta.dataset.noun;
    this.refs.status.textContent = `${tab.dataset.label}: ${shown} ${noun}${brand ? ` · ${brand}` : ''}`;
  }

  /**
   * Keep the tallest tab state: open every tab once (no paint in between), each with the help line's words it
   * shows (#onShow: a longer tool link can wrap the help line once more), take the section's height, restore the
   * open tab and its words, and hold that height as min-height. A shorter tab's slack falls between the rail and
   * the help line (bl-shelf.css: a column, the help line at the foot), so the module below never moves. A tab on
   * demand is measured with its placeholder cards (the card's geometry), so this holds before any tab has come.
   */
  #measure() {
    const tabs = (this.refs.tabs || []).filter((tab) => !tab.hidden);
    if (!tabs.length) return;
    const link = this.refs.helpTool;
    const words = link?.textContent || '';
    // what the reveals would cost: the tabs row's scroll (another tab's door changes the row's room) and the open
    // rail's (hidden for a moment); both are put back before anything paints
    const row = tabs[0].parentElement;
    const rowScroll = row ? row.scrollLeft : 0;
    const track = this.#trackOf(this.#current);
    const railScroll = track ? track.scrollLeft : 0;
    this.style.minHeight = '';
    let height = 0;
    for (const tab of tabs) {
      const key = tab.dataset.tab || '';
      this.#reveal(key);
      const tool = link ? tab.dataset.helpTool || link.dataset.default || words : '';
      if (link && link.textContent !== tool) link.textContent = tool;
      const state = this.getBoundingClientRect().height;
      this.#states.set(key, state);
      height = Math.max(height, state);
    }
    this.#reveal(this.#current);
    if (link && link.textContent !== words) link.textContent = words;
    this.style.minHeight = `${Math.ceil(height)}px`;
    if (row && row.scrollLeft !== rowScroll) row.scrollLeft = rowScroll;
    if (track && track.scrollLeft !== railScroll) {
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = railScroll;
      track.style.scrollBehavior = '';
    }
  }

  #onResize = () => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => {
      const width = this.getBoundingClientRect().width;
      if (Math.abs(width - this.#width) < 1) return;   // a phone's toolbar changing the height is not a new layout
      this.#width = width;
      this.#measure();
      const tab = (this.refs.tabs || []).find((item) => item.dataset.tab === this.#current);
      if (tab) this.#follow(tab);
    }, 150);
  };

  /**
   * The help line's tool link takes the open tab's words and page (or its own defaults).
   * @param {Event} event
   */
  #onShow = (event) => {
    const link = this.refs.helpTool;
    if (!link || !(event instanceof CustomEvent)) return;
    const href = event.detail?.toolUrl || link.dataset.defaultUrl || '';
    if (href && link.getAttribute('href') !== href) link.setAttribute('href', href);
    const words = event.detail?.tool || link.dataset.default || '';
    if (words && link.textContent !== words) link.textContent = words;
  };

  /**
   * Theme editor: selecting a tab block opens that tab.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const key = target.closest('[role="tab"]')?.getAttribute('data-tab');
    if (key && key !== this.#current) this.#show(key);
  };

  /** @param {string} key */
  #trackOf(key) {
    const panel = this.#byTab(this.refs.panels, key);
    const track = panel?.querySelector('.bl-shelf__rail');
    return track instanceof HTMLElement ? track : null;
  }

  /**
   * @template {HTMLElement} T
   * @param {T[] | undefined} list
   * @param {string} key
   * @returns {T | undefined}
   */
  #byTab(list, key) {
    return (list || []).find((element) => element.dataset.tab === key);
  }
}

if (!customElements.get('bl-shelf-component')) {
  customElements.define('bl-shelf-component', BlShelfComponent);
}

export { BlShelfComponent };
